import { apiCustomer, apiCustomerPublic } from "@/api"
import { storedCampaignCode } from "@/domain/attribution/attribution"
import { getMerchantId } from "@/lib/company-storage"
import { resolveStorefrontSubdomain } from "@/lib/ensure-company"
import { storefrontDeviceId } from "./prize-device"

/** One prize of a game as the storefront sees it; odds and stock never reach the client. */
export type PrizeOption = { id: string; label: string; artKey?: string }

/** The merchant's active prize game (`GET /storefront/:subdomain/prize-game`); `type` is `SCRATCH_CARD` or `WHEEL`, read through `prizeGameKind`. */
export type PrizeGame = {
  id: string
  name: string
  type: string
  active: boolean
  periodDays: number
  prizes: PrizeOption[]
  missPossible?: boolean
}

/** Why the server answered a draw calmly without drawing. */
export type PrizeLimitReason = "PHONE_ALREADY_PLAYED" | "IP_DAILY_CAP" | "DEVICE_DAILY_CAP" | "PRIOR_ORDER_REQUIRED"

/** Result of one server-side draw; `replayed` returns the window's earlier draw and `limited` drew nothing, saying why in `limitReason`. */
export type PrizeDraw = {
  drawId: string
  won: boolean
  prizeId?: string
  prizeLabel?: string
  prizeArtKey?: string
  code?: string
  replayed: boolean
  limited?: boolean
  limitReason?: PrizeLimitReason | string
  nextChanceAt?: string
  drawnAt?: string
  redeemed?: boolean
}

/** Whether the logged customer may play now (`GET /storefront/:subdomain/prize-game/status`), with their draw still inside the window. */
export type PrizeDrawStatus = {
  gameId: string
  canPlay: boolean
  nextChanceAt?: string
  limitReason?: PrizeLimitReason | string
  lastDraw?: PrizeDraw
}

/** A prize the customer won and may still redeem (`GET /storefront/:subdomain/prize-game/prizes`); `code` stays empty while the coupon is being issued, `expiresAt` is an ISO instant. */
export type WonPrize = {
  drawId: string
  gameType: string
  gameName: string
  prizeLabel: string
  prizeArtKey?: string
  code: string
  wonAt: string
  expiresAt: string
}

/** The customer's referral link (`GET /storefront/:subdomain/referral`). */
export type ReferralLink = { code: string; url: string; welcomeBenefitText?: string; rewardBenefitText?: string }

/** Body of the abandoned-cart enrolment. */
export type CartAbandonedPayload = {
  consent: boolean
  cartValueCents: number
  sessionId: string
  favoriteItem: string
  abandonedAt?: string
}

function storefrontPath(suffix: string): string {
  return `/storefront/${encodeURIComponent(resolveStorefrontSubdomain())}${suffix}`
}

/** The campaign code of the visitor's stored attribution, sent so a campaign-link-only game is offered. */
function visitorCampaignCode(): string | undefined {
  return storedCampaignCode(getMerchantId())
}

/** Active prize game offered to this visitor (campaign-link-only games need the stored `mc`), or null when none runs, it is not offered or the read fails. */
export async function fetchPrizeGame(): Promise<PrizeGame | null> {
  try {
    const { data } = await apiCustomerPublic.get<PrizeGame>(storefrontPath("/prize-game"), { params: { mc: visitorCampaignCode() } })
    return data?.active && data.prizes?.length ? data : null
  } catch {
    return null
  }
}

/** Draws server-side, sending the stored campaign code and this browser's device id; the outcome is never decided on the client. */
export async function drawPrize(gameId?: string): Promise<PrizeDraw> {
  const mc = visitorCampaignCode()
  const deviceId = storefrontDeviceId()
  const { data } = await apiCustomer.post<PrizeDraw>(storefrontPath("/prize-game/draw"), {
    ...(gameId ? { gameId } : {}),
    ...(mc ? { mc } : {}),
    ...(deviceId ? { deviceId } : {}),
  })
  return data
}

/** The logged customer's standing in the game, or null when it cannot be read (the draw stays authoritative). */
export async function fetchPrizeDrawStatus(gameId?: string): Promise<PrizeDrawStatus | null> {
  try {
    const { data } = await apiCustomer.get<PrizeDrawStatus>(storefrontPath("/prize-game/status"), { params: { gameId, mc: visitorCampaignCode() } })
    return data && typeof data.canPlay === "boolean" ? data : null
  } catch {
    return null
  }
}

function wonPrizeOf(raw: unknown): WonPrize | null {
  if (typeof raw !== "object" || raw === null) return null
  const entry: Partial<Record<keyof WonPrize, unknown>> = raw
  const field = (value: unknown) => (typeof value === "string" ? value : "")
  const prize: WonPrize = {
    drawId: field(entry.drawId),
    gameType: field(entry.gameType),
    gameName: field(entry.gameName),
    prizeLabel: field(entry.prizeLabel),
    ...(field(entry.prizeArtKey) ? { prizeArtKey: field(entry.prizeArtKey) } : {}),
    code: field(entry.code),
    wonAt: field(entry.wonAt),
    expiresAt: field(entry.expiresAt),
  }
  return prize.drawId ? prize : null
}

/** The customer's won prizes at this store, newest first; null when the read fails, so a caller can tell "none" from "unknown". */
export async function fetchWonPrizes(): Promise<WonPrize[] | null> {
  try {
    const { data } = await apiCustomer.get<unknown>(storefrontPath("/prize-game/prizes"))
    const list = typeof data === "object" && data !== null && "prizes" in data && Array.isArray(data.prizes) ? data.prizes : []
    return list.flatMap((raw: unknown) => wonPrizeOf(raw) ?? [])
  } catch {
    return null
  }
}

/** The customer's referral link, or null when the merchant has no referral program. */
export async function fetchReferralLink(): Promise<ReferralLink | null> {
  try {
    const { data } = await apiCustomer.get<ReferralLink>(storefrontPath("/referral"))
    return data?.code && data.url ? data : null
  } catch {
    return null
  }
}

/** Enrols the abandoned cart in the reminder flow; resolves to whether the customer was enrolled. */
export async function postCartAbandoned(payload: CartAbandonedPayload): Promise<boolean> {
  const { data } = await apiCustomer.post<{ enrolled: boolean }>(storefrontPath("/cart-abandoned"), payload)
  return Boolean(data?.enrolled)
}
